// frontend/src/components/leads/AddLead.js

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import Layout from "../common/Layout";
import DmsSelector from "../dms/DmsSelector";
import ProductSelector from "../products/ProductSelector";
import ContactList from "../contacts/ContactList";
import DocumentUpload from "../documents/DocumentUpload";
import DocumentList from "../documents/DocumentList";
import { useAuth } from "../../contexts/AuthContext";

// [D10] Field validators — optional-field checks (empty is valid)
import {
  optionalEmail,
  optionalPhone,
  optionalUrl,
  optionalZip,
  optionalPositiveInt,
  optionalDate,
} from "../../utils/fieldValidators";

// [D10] emptyContact now accepts isPrimary. Default false so
// existing callers still work identically.
const emptyContact = (isPrimary = false) => ({
  firstName: "",
  lastName: "",
  title: "",
  workPhone: "",
  homePhone: "",
  email: "",
  mobilePhone: "",
  isPrimary,
});

/*
 * Filter a master data list down to rows the user is allowed
 * to select. System rows (hideFromUser = true, isSystem = true)
 * are excluded.
 */
const selectableRows = (list) =>
  Array.isArray(list)
    ? list.filter((row) => !row?.hideFromUser)
    : [];

function AddLead() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [leadSources, setLeadSources] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [selectableLeadTypes, setSelectableLeadTypes] = useState([]);
  const [serviceDashboards, setServiceDashboards] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [selectableLeadStatuses, setSelectableLeadStatuses] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [dealershipExpanded, setDealershipExpanded] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);

  const [formData, setFormData] = useState({
    company: "",
    salesRepId: user?.userId ? String(user.userId) : "",
    dmsProviders: [],
    otherDmsProvider: "",
    dealerGroup: "",
    manufacturer: "",
    rooftops: null,
    mainLine: "",
    email: "",
    website: "",
    streetAddress: "",
    state: "",
    city: "",
    zipCode: "",
    country: "USA",
    status: "New",
    leadSource: "None",
    leadType: "New",
    serviceDashboard: "None",
    contactDate: "",
    opportunityYn: null,
    products: [],
    notes: "",
    nextSteps: "",
    // [D10] first contact is primary by default
    contacts: [emptyContact(true)],
  });

  useEffect(() => {
    Promise.all([
      api.getMasterData("leadSources"),
      api.getMasterData("leadTypes"),
      api.getMasterData("serviceDashboards"),
      api.getMasterData("leadStatuses"),
    ])
      .then(([sources, types, dashboards, statuses]) => {
        const sourceList = Array.isArray(sources) ? sources : [];
        const typeList = Array.isArray(types) ? types : [];
        const dashboardList = Array.isArray(dashboards) ? dashboards : [];
        const statusList = Array.isArray(statuses) ? statuses : [];

        setLeadSources(sourceList);
        setLeadTypes(typeList);
        setSelectableLeadTypes(selectableRows(typeList));

        setServiceDashboards(dashboardList);

        setLeadStatuses(statusList);
        setSelectableLeadStatuses(selectableRows(statusList));

        if (
          ["SM", "Admin", "Sales Owner", "Sales Manager"].includes(user?.role)
        ) {
          api
            .getSalesReps()
            .then((reps) => setSalesReps(Array.isArray(reps) ? reps : []))
            .catch(console.error);
        }
      })
      .catch(console.error);
  }, [user?.userId, user?.role]);

  /* R2 — auto-select if only one source allowed */
  useEffect(() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return;
      const u = JSON.parse(raw);
      const scope = u?.allowedLeadSources;
      if (Array.isArray(scope) && scope.length === 1 && !formData.leadSource) {
        setField("leadSource", scope[0]);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const dmsChange = (v) => {
    const arr = Array.isArray(v) ? v : [];
    const other = arr.find((x) => String(x).startsWith("Other: "));
    setFormData((prev) => ({
      ...prev,
      dmsProviders: arr.filter(
        (x) => x !== "Other" && !String(x).startsWith("Other: "),
      ),
      otherDmsProvider: other ? String(other).substring(7) : "",
    }));
  };

  // [D10] Extended validation
  const validate = () => {
    const e = {};

    // ----- Required -----
    if (!formData.company.trim()) e.company = "Dealership name is required";

    // ----- Dealership optional fields (only validated when non-empty) -----
    if (!optionalEmail(formData.email))
      e.email = "Enter a valid dealership email";
    if (!optionalUrl(formData.website))
      e.website = "Enter a valid website URL";
    if (!optionalPhone(formData.mainLine))
      e.mainLine = "Enter a valid main line number";
    if (!optionalZip(formData.zipCode))
      e.zipCode = "Enter a valid ZIP code";
    if (!optionalPositiveInt(formData.rooftops))
      e.rooftops = "Rooftops must be a positive number";
    if (!optionalDate(formData.contactDate, { notFuture: true }))
      e.contactDate = "Contact date must be today or earlier";

    // ----- Contacts -----
    const validContacts = formData.contacts.filter((c) =>
      c.firstName?.trim(),
    );
    if (!validContacts.length) {
      e.contacts = "At least one contact with a name is required";
    }

    formData.contacts.forEach((c, i) => {
      if (!c.firstName?.trim())
        e[`contact_${i}_firstName`] = "Contact first name is required";
      if (!optionalEmail(c.email))
        e[`contact_${i}_email`] = "Invalid email";
      if (!optionalPhone(c.workPhone))
        e[`contact_${i}_workPhone`] = "Invalid work phone";
      if (!optionalPhone(c.homePhone))
        e[`contact_${i}_homePhone`] = "Invalid home phone";
      if (!optionalPhone(c.mobilePhone))
        e[`contact_${i}_mobilePhone`] = "Invalid mobile phone";
    });

    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      let rooftops = formData.rooftops;
      if (
        rooftops === "" ||
        rooftops === null ||
        rooftops === undefined ||
        isNaN(rooftops)
      ) {
        rooftops = null;
      } else {
        rooftops = Number(rooftops);
      }

      // [D10] Normalize contacts: exactly one primary.
      // If the user explicitly chose one, respect it.
      // Otherwise, the first valid contact becomes primary.
      const validContacts = formData.contacts.filter((c) =>
        c.firstName?.trim(),
      );
      const anyPrimary = validContacts.some((c) => c.isPrimary);
      const normalizedContacts = validContacts.map((c, i) => ({
        firstName: c.firstName,
        lastName: c.lastName,
        title: c.title,
        workPhone: c.workPhone,
        homePhone: c.homePhone,
        email: c.email,
        mobilePhone: c.mobilePhone,
        isPrimary: anyPrimary ? !!c.isPrimary : i === 0,
      }));

      const result = await api.createLead({
        ...formData,
        company: formData.company.trim(),
        salesRepId: formData.salesRepId || undefined,
        dmsProviders: formData.dmsProviders,
        otherDmsProvider: formData.otherDmsProvider.trim(),
        rooftops: rooftops,
        contacts: normalizedContacts,
      });

      if (result?.lead_id) {
        if (pendingFiles.length > 0) {
          try {
            const formDataUpload = new FormData();
            pendingFiles.forEach((f, index) => {
              formDataUpload.append("documents", f.file);
              formDataUpload.append(
                `documentTypes[${index}]`,
                f.documentType || "Other",
              );
              formDataUpload.append(
                `descriptions[${index}]`,
                f.description || "",
              );
            });
            await api.uploadDocuments(result.lead_id, formDataUpload);
          } catch (uploadErr) {
            console.error("Failed to upload documents:", uploadErr);
          }
        }
        navigate(`/leads/${result.lead_id}/demo`);
      } else {
        throw new Error("Lead was not created");
      }
    } catch (err) {
      console.error("Create lead error:", err);
      setErrors({
        form: err.response?.data?.error || "Failed to create lead",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleDealership = () => {
    setDealershipExpanded((prev) => !prev);
  };

  const handleFilesChange = (files) => {
    setPendingFiles(files || []);
  };

  // ========== SECTION HEADER ==========
  const SectionHeader = ({ number, title, subtitle, required = false }) => (
    <div className="form-section-header">
      <span className="section-number">{number}</span>
      <div>
        <h3>
          {title}
          {required && <span className="required">*</span>}
        </h3>
        {subtitle && <p className="section-subtitle">{subtitle}</p>}
      </div>
    </div>
  );

  return (
    <Layout
      title="New Lead"
      subtitle="Add the essentials, file the lead"
      actions={
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate("/leads")}
          disabled={submitting}
        >
          Cancel
        </button>
      }
    >
      <form onSubmit={submit} className="lead-form">
        {errors.form && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {errors.form}
          </div>
        )}

        {/* SECTION 1: DEALERSHIP */}
        <div className="form-section">
          <SectionHeader
            number="1"
            title="Dealership"
            subtitle="Enter the dealership details and DMS provider"
            required
          />

          <div className="form-group">
            <label>
              Dealership name <span className="required">*</span>
            </label>
            <input
              className={`form-control ${errors.company ? "error" : ""}`}
              value={formData.company}
              onChange={(e) => setField("company", e.target.value)}
              placeholder="Search or add a dealership..."
              required
            />
            {errors.company && (
              <div className="error-text">{errors.company}</div>
            )}
          </div>

          <DmsSelector
            selected={[
              ...formData.dmsProviders,
              ...(formData.otherDmsProvider
                ? [`Other: ${formData.otherDmsProvider}`]
                : []),
            ]}
            onChange={dmsChange}
            error={errors.dmsProviders}
          />

          <div className="form-group">
            <label>Sales Agent / Owner</label>
            {["SM", "Admin", "Sales Owner", "Sales Manager"].includes(
              user?.role,
            ) ? (
              <select
                className="form-control"
                value={formData.salesRepId}
                onChange={(e) => setField("salesRepId", e.target.value)}
              >
                {user?.userId && (
                  <option value={String(user.userId)}>
                    {user.fullName || "Logged-in User"} (Me)
                  </option>
                )}
                {salesReps
                  .filter((rep) => String(rep.user_id) !== String(user?.userId))
                  .map((rep) => (
                    <option key={rep.user_id} value={rep.user_id}>
                      {rep.full_name}
                    </option>
                  ))}
              </select>
            ) : (
              <input
                className="form-control"
                value={user?.fullName || ""}
                readOnly
              />
            )}
            <small className="text-muted">
              Ownership is stored using the CRM User ID (partner_id).
            </small>
          </div>

          <button
            type="button"
            className="btn-toggle-details"
            onClick={toggleDealership}
          >
            <i
              className={`fas fa-${dealershipExpanded ? "minus" : "plus"}`}
            ></i>
            <span>
              {dealershipExpanded ? "Hide" : "Add"} dealership details
            </span>
            <span className="optional-text">Optional</span>
          </button>

          {dealershipExpanded && (
            <div className="dealership-details-panel">
              <div className="form-row">
                <div className="form-group">
                  <label>Dealer Group</label>
                  <input
                    className="form-control"
                    value={formData.dealerGroup}
                    onChange={(e) => setField("dealerGroup", e.target.value)}
                    placeholder="Search or add a group..."
                  />
                </div>
                <div className="form-group">
                  <label>Manufacturer</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.manufacturer}
                    onChange={(e) => setField("manufacturer", e.target.value)}
                    placeholder="Enter manufacturer"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label># of Rooftops</label>
                  <input
                    type="number"
                    className={`form-control ${errors.rooftops ? "error" : ""}`}
                    value={formData.rooftops === null ? "" : formData.rooftops}
                    onChange={(e) => {
                      const val = e.target.value;
                      setField("rooftops", val === "" ? null : Number(val));
                    }}
                    placeholder="Optional"
                    min="1"
                  />
                  {errors.rooftops ? (
                    <div className="error-text">{errors.rooftops}</div>
                  ) : (
                    <small className="text-muted">
                      Leave empty if not applicable
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Dealer Main Line</label>
                  <input
                    className={`form-control ${errors.mainLine ? "error" : ""}`}
                    value={formData.mainLine}
                    onChange={(e) => setField("mainLine", e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                  {errors.mainLine && (
                    <div className="error-text">{errors.mainLine}</div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Dealership Email</label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? "error" : ""}`}
                    value={formData.email}
                    onChange={(e) => setField("email", e.target.value)}
                    placeholder="service@dealership.com"
                  />
                  {errors.email && (
                    <div className="error-text">{errors.email}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input
                    className={`form-control ${errors.website ? "error" : ""}`}
                    value={formData.website}
                    onChange={(e) => setField("website", e.target.value)}
                    placeholder="dealership.com"
                  />
                  {errors.website && (
                    <div className="error-text">{errors.website}</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Street Address</label>
                <input
                  className="form-control"
                  value={formData.streetAddress}
                  onChange={(e) => setField("streetAddress", e.target.value)}
                  placeholder="1450 Ogden Ave"
                />
                <small className="text-muted">
                  Optional — it helps the team place the store
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    className="form-control"
                    value={formData.city}
                    onChange={(e) => setField("city", e.target.value)}
                    placeholder="Naperville"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    className="form-control"
                    value={formData.state}
                    onChange={(e) => setField("state", e.target.value)}
                    placeholder="IL"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>ZIP</label>
                  <input
                    className={`form-control ${errors.zipCode ? "error" : ""}`}
                    value={formData.zipCode}
                    onChange={(e) => setField("zipCode", e.target.value)}
                    placeholder="60563"
                  />
                  {errors.zipCode && (
                    <div className="error-text">{errors.zipCode}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input
                    className="form-control"
                    value={formData.country}
                    onChange={(e) => setField("country", e.target.value)}
                    placeholder="USA"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SECTION 2: LEAD INFORMATION */}
        <div className="form-section">
          <SectionHeader
            number="2"
            title="Lead Information"
            subtitle="Define the lead's status, source, and opportunity"
          />

          <div className="form-row">
            <div className="form-group">
              <label>Lead Status</label>
              <select
                className="form-control"
                value={formData.status}
                onChange={(e) => setField("status", e.target.value)}
              >
                {selectableLeadStatuses.map((x) => (
                  <option key={x.id || x.name} value={x.name}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Lead Type</label>
              <select
                className="form-control"
                value={formData.leadType}
                onChange={(e) => setField("leadType", e.target.value)}
              >
                {selectableLeadTypes.map((x) => (
                  <option key={x.id || x.name} value={x.name}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Lead Source</label>
              <select
                className="form-control"
                value={formData.leadSource}
                onChange={(e) => setField("leadSource", e.target.value)}
              >
                <option value="">Select Lead Source</option>
                {leadSources
                  .filter((x) => {
                    try {
                      const raw = localStorage.getItem("user");
                      if (!raw) return true;
                      const u = JSON.parse(raw);
                      const scope = u?.allowedLeadSources;
                      if (scope === null || scope === undefined) return true;
                      if (!Array.isArray(scope)) return true;
                      if (scope.length === 0) return false;
                      return scope.includes(x.name);
                    } catch {
                      return true;
                    }
                  })
                  .map((x) => (
                    <option key={x.id} value={x.name}>
                      {x.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label>Choose Service Dashboard</label>
              <select
                className="form-control"
                value={formData.serviceDashboard}
                onChange={(e) => setField("serviceDashboard", e.target.value)}
              >
                {serviceDashboards.map((x) => (
                  <option key={x.id} value={x.name}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact Date</label>
              <input
                type="date"
                className={`form-control ${errors.contactDate ? "error" : ""}`}
                value={formData.contactDate}
                onChange={(e) => setField("contactDate", e.target.value)}
              />
              {errors.contactDate && (
                <div className="error-text">{errors.contactDate}</div>
              )}
            </div>
            <div className="form-group">
              <label>Opportunity</label>
              <select
                className="form-control"
                value={formData.opportunityYn ?? ""}
                onChange={(e) =>
                  setField("opportunityYn", e.target.value || null)
                }
              >
                <option value="">Not Set</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Next Steps</label>
            <textarea
              className="form-control"
              rows="3"
              value={formData.nextSteps}
              onChange={(e) => setField("nextSteps", e.target.value)}
              placeholder="What should happen next?"
              maxLength="2000"
            />
          </div>
        </div>

        {/* SECTION 3: CONTACTS */}
        <div className="form-section">
          <SectionHeader
            number="3"
            title="Contacts"
            subtitle="Add one or more contacts for this dealership"
            required
          />

          <ContactList
            contacts={formData.contacts}
            onChange={(v) => setField("contacts", v)}
            error={errors.contacts}
          />

          {/* [D10] per-contact inline error summary */}
          {formData.contacts.map((c, i) => {
            const contactErrors = [
              "firstName",
              "email",
              "workPhone",
              "homePhone",
              "mobilePhone",
            ]
              .map((f) => errors[`contact_${i}_${f}`])
              .filter(Boolean);
            if (!contactErrors.length) return null;
            return (
              <div
                key={`error-${i}`}
                className="error-text"
                style={{ marginTop: "4px" }}
              >
                Contact {i + 1}: {contactErrors.join(", ")}
              </div>
            );
          })}
        </div>

        {/* SECTION 4: PRODUCTS */}
        <div className="form-section">
          <SectionHeader
            number="4"
            title="Products"
            subtitle="Select the products this lead is interested in"
          />

          <ProductSelector
            selected={formData.products}
            onChange={(v) => setField("products", v)}
          />
        </div>

        {/* SECTION 5: DOCUMENTS */}
        <div className="form-section">
          <SectionHeader
            number="5"
            title="Documents"
            subtitle="Attach relevant files to this lead"
          />

          <DocumentUpload
            leadId={null}
            onUploadComplete={(uploadedDocs) => {
              setDocuments((prev) => [...prev, ...uploadedDocs]);
              setPendingFiles([]);
            }}
            onFilesChange={handleFilesChange}
          />

          {documents.length > 0 && (
            <DocumentList
              documents={documents}
              leadId={null}
              onDelete={(docId) => {
                setDocuments((prev) =>
                  prev.filter((d) => d.document_id !== docId),
                );
              }}
            />
          )}
        </div>

        {/* SECTION 6: NOTES */}
        <div className="form-section">
          <SectionHeader
            number="6"
            title="Notes"
            subtitle="Add any additional context about this lead"
          />

          <div className="form-group">
            <label>Notes to IVSG</label>
            <textarea
              className="form-control"
              rows="4"
              value={formData.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Add any context IVSG should know about this lead."
              maxLength="2000"
            />
            <small className="text-muted">
              Shared with IVSG and kept with the lead
            </small>
          </div>
        </div>

        {/* FORM ACTIONS */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/leads")}
            disabled={submitting}
          >
            Cancel
          </button>
          <button className="btn btn-primary" disabled={submitting}>
            {submitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fas fa-handshake"></i> File lead & book demo
              </>
            )}
          </button>
        </div>
      </form>
    </Layout>
  );
}

export default AddLead;